export default function initStarRating(input, options = {}) {
    if (!input || input.nodeType !== 1) {
        return null;
    }

    if (input.__starRatingInstance) {
        return input.__starRatingInstance;
    }

    const config = {
        stars: 10,
        min: 1,
        max: null,
        step: 1,
        ...options
    };

    const min = Number.isFinite(Number(config.min)) ? Number(config.min) : 1;
    const step = Math.abs(Number(config.step)) || 1;
    const starCount = Math.max(1, Math.round(Number(config.stars) || 10));
    const max = Number.isFinite(Number(config.max)) ? Number(config.max) : min + (starCount - 1) * step;
    const normalizedMin = Math.min(min, max);
    const normalizedMax = Math.max(min, max);

    const label = getLabel(input);
    const root = document.createElement('div');
    const buttons = [];
    const state = {
        hoverValue: null
    };

    root.className = 'star-rating';
    root.setAttribute('role', 'radiogroup');
    root.setAttribute('aria-label', label);

    input.classList.add('star-rating__input');
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');
    input.insertAdjacentElement('afterend', root);

    for (let index = 0; index < starCount; index += 1) {
        const value = getStarValue(index, starCount, normalizedMin, normalizedMax, step);
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'star-rating__star';
        button.dataset.value = String(value);
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-label', `${value} star${value === 1 ? '' : 's'}`);
        button.addEventListener('click', () => {
            setValue(value, { emit: true, focus: true });
        });
        button.addEventListener('mouseenter', () => {
            setHoverValue(value);
        });
        button.addEventListener('focus', () => {
            clearHoverValue();
        });

        buttons.push(button);
        root.appendChild(button);
    }

    root.addEventListener('mouseleave', clearHoverValue);
    root.addEventListener('keydown', onKeyDown);

    const initialValue = input.value === '' ? normalizedMin : input.value;
    setValue(initialValue, { emit: false });

    const instance = {
        root,
        input,
        setValue,
        destroy
    };

    input.__starRatingInstance = instance;

    return instance;

    function getLabel(source) {
        const ariaLabel = source.getAttribute('aria-label');
        if (ariaLabel) {
            return ariaLabel;
        }

        if (source.labels && source.labels.length > 0) {
            const text = source.labels[0].textContent || '';
            const trimmed = text.replace(/\s+/g, ' ').trim();
            if (trimmed) {
                return trimmed;
            }
        }

        return source.name || 'Rating';
    }

    function getStarValue(index, count, minValue, maxValue, increment) {
        if (count === 1) {
            return clampValue(maxValue);
        }

        const ratio = index / (count - 1);
        const rawValue = minValue + ((maxValue - minValue) * ratio);
        return clampValue(alignToStep(rawValue, minValue, increment), minValue, maxValue);
    }

    function alignToStep(value, minValue, increment) {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
            return minValue;
        }

        const aligned = minValue + (Math.round((numericValue - minValue) / increment) * increment);
        return Number.isFinite(aligned) ? aligned : minValue;
    }

    function clampValue(value, lowerBound = normalizedMin, upperBound = normalizedMax) {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
            return lowerBound;
        }

        return Math.min(upperBound, Math.max(lowerBound, numericValue));
    }

    function normalizeValue(value) {
        return clampValue(alignToStep(value, normalizedMin, step));
    }

    function setValue(value, { emit = true, focus = false } = {}) {
        const nextValue = normalizeValue(value);

        input.value = String(nextValue);
        render(nextValue);

        if (focus) {
            focusSelected();
        }

        if (emit) {
            dispatchUpdateEvents();
        }

        return nextValue;
    }

    function render(selectedValue) {
        const hoveredValue = Number.isFinite(state.hoverValue) ? state.hoverValue : null;

        buttons.forEach((button) => {
            const buttonValue = Number(button.dataset.value);
            const active = buttonValue <= selectedValue;
            const hovered = hoveredValue !== null && buttonValue <= hoveredValue;

            button.classList.toggle('is-active', active);
            button.classList.toggle('is-hover', hovered && !active);
            button.setAttribute('aria-checked', active ? 'true' : 'false');
            button.tabIndex = buttonValue === selectedValue ? 0 : -1;
        });
    }

    function setHoverValue(value) {
        state.hoverValue = normalizeValue(value);
        render(Number(input.value));
    }

    function clearHoverValue() {
        if (state.hoverValue === null) {
            return;
        }

        state.hoverValue = null;
        render(Number(input.value));
    }

    function focusSelected() {
        const selectedButton = buttons.find((button) => Number(button.dataset.value) === Number(input.value));
        if (selectedButton) {
            selectedButton.focus({ preventScroll: true });
        }
    }

    function dispatchUpdateEvents() {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function onKeyDown(event) {
        const currentValue = Number(input.value) || normalizedMin;

        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            event.preventDefault();
            setValue(currentValue + step, { emit: true, focus: true });
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            event.preventDefault();
            setValue(currentValue - step, { emit: true, focus: true });
        } else if (event.key === 'Home') {
            event.preventDefault();
            setValue(normalizedMin, { emit: true, focus: true });
        } else if (event.key === 'End') {
            event.preventDefault();
            setValue(normalizedMax, { emit: true, focus: true });
        }
    }

    function destroy() {
        root.removeEventListener('mouseleave', clearHoverValue);
        root.removeEventListener('keydown', onKeyDown);
        delete input.__starRatingInstance;
    }
}
